// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Button,
  Flex,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import type { Spacing } from '@iheartjane/reefer';
import { mediaQueries, spacing } from '@iheartjane/reefer-emotion';
import debounce from 'lodash-es/debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { DEFAULT_DESKTOP_CARD_WIDTH } from '@jane/shared/components';

const DEFAULT_MOBILE_CAROUSEL_CARD_WIDTH = 175;

const ArrowContainer = styled.div<{
  arrowOffset?: number;
  left?: boolean;
}>(
  ({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute' as const,
    zIndex: 1,
    backgroundColor: theme.colors.grays.white,
    width: 48,
    height: 48,
    borderRadius: '50%',
    [mediaQueries.desktop('sm', 'max')]: {
      display: 'none',
    },
  }),
  ({ left, arrowOffset }) => [
    left && { left: arrowOffset ? -arrowOffset : -22 },
    { right: arrowOffset ? -arrowOffset : -22 },
  ]
);

const ItemsContainer = styled(Flex)<{
  blockDesktopScrolling?: boolean;
}>(
  {
    overflowX: 'scroll',
    scrollbarWidth: 'none',
    MsOverflowStyle: 'none',
    WebkitOverflowScrolling: 'touch',
    '&::-webkit-scrollbar': {
      width: 0,
    },
  },
  ({ blockDesktopScrolling }) => [
    blockDesktopScrolling && {
      [mediaQueries.desktop('sm', 'min')]: { overflowX: 'clip' },
    },
  ]
);

const CardsContainer = styled.div<{
  dynamicWidth?: boolean;
  flexBasis?: string;
  itemWidth?: number;
  translateAmount: number;
}>(
  spacing({ py: 4, mx: 12 }),
  {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoRows: '1fr',
    gridGap: '24px 24px',
    width: '100%',
    transition: 'all 500ms',
    [mediaQueries.mobile('lg', 'max')]: {
      // NOTE(elliot): On mobile there is no column gap at the end.
      '&:after': {
        content: '""',
        width: '0.01px',
      },
    },
  },
  ({ dynamicWidth, translateAmount, itemWidth, flexBasis }) => ({
    transform: `translateX(-${translateAmount}px)`,
    flexBasis: flexBasis || 'auto',
    gridAutoColumns: dynamicWidth
      ? '1fr'
      : `${itemWidth || DEFAULT_DESKTOP_CARD_WIDTH}px`,
    [mediaQueries.tablet('max')]: {
      gridAutoColumns: dynamicWidth
        ? '1fr'
        : `${itemWidth || DEFAULT_MOBILE_CAROUSEL_CARD_WIDTH}px`,
    },
  })
);

interface RenderProps<T> {
  index: number;
  item: T;
  itemWidth?: number;
}

interface ControlledIndex {
  currentIndex: number | null /* null if scrolling is allowed eg mobile */;
  onShowRightArrow: (showRightArrow: boolean) => void;
}

interface Props<T> {
  alwaysUseScrolling?: boolean;
  arrowOffset?: number;
  controlledIndex?: ControlledIndex /* allow controlling scroll/paging index externally */;
  dynamicWidth?: boolean;
  flexBasis?: string;
  itemMargin: number;
  itemRenderer: (arg: RenderProps<T>) => ReactNode;
  items: readonly T[];
}

export const ItemsCarousel = <T extends object>({
  items,
  itemRenderer,
  itemMargin,
  arrowOffset,
  alwaysUseScrolling,
  dynamicWidth,
  controlledIndex,
  flexBasis,
}: Props<T>) => {
  const isMobile = useMobileMediaQuery({});

  const [maxScrollWidth, setMaxScrollWidth] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(
    controlledIndex ? controlledIndex?.currentIndex : 0
  );

  const [itemWidth, setItemWidth] = useState(0);

  const carousel = useRef<HTMLDivElement | null>(null);
  const cardGap = useMemo(() => itemMargin * 2, [itemMargin]);
  const updateMaxWidth = useCallback(
    () =>
      carousel.current &&
      setMaxScrollWidth(
        carousel.current?.scrollWidth - carousel.current?.offsetWidth
      ),
    []
  );

  useEffect(() => {
    updateMaxWidth();
  }, [itemWidth, updateMaxWidth, items]);

  useEffect(() => {
    // NOTE(elliot): When item width is updated, the cards should shrink or grow to fit new space.
    const onResize = debounce(updateMaxWidth, 300);
    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    // NOTE(elliot): Should only widen item width to fill space when there is overflow and we don't want to show a cut off card.
    if (carousel.current?.scrollWidth === carousel.current?.offsetWidth) {
      return;
    }

    const defaultCardWidth = isMobile
      ? Math.min(
          DEFAULT_MOBILE_CAROUSEL_CARD_WIDTH,
          (carousel.current?.offsetWidth || 0) / 2
        )
      : DEFAULT_DESKTOP_CARD_WIDTH + cardGap;

    const carouselWidth = carousel.current!.offsetWidth;
    const newItemWidth =
      carouselWidth / Math.floor(carouselWidth / defaultCardWidth) - cardGap;

    if (newItemWidth !== itemWidth) {
      // NOTE(elliot): When the carousel is in the store menu, we have to sync the card widths even if some rows don't fill all the space.
      setItemWidth(newItemWidth);
    }
  }, [
    carousel.current?.scrollWidth,
    carousel.current?.offsetWidth,
    cardGap,
    isMobile,
    itemWidth,
    maxScrollWidth,
  ]);

  const carouselItems = useMemo(
    () =>
      items.map((item: T, index: number) =>
        itemRenderer({
          item,
          index,
          itemWidth,
        })
      ),
    [items, itemRenderer, itemWidth]
  );

  useEffect(() => {
    if (controlledIndex) setCurrentIndex(controlledIndex?.currentIndex);
  }, [controlledIndex?.currentIndex]);

  const showArrows = !controlledIndex && !alwaysUseScrolling;
  const showRightArrow = carousel.current
    ? carousel.current.offsetWidth * (currentIndex ?? 0) < maxScrollWidth
    : false;
  const showLeftArrow = (currentIndex ?? 0) > 0;

  useEffect(() => {
    if (controlledIndex?.onShowRightArrow)
      controlledIndex?.onShowRightArrow(showRightArrow);
  }, [controlledIndex?.onShowRightArrow, showRightArrow]);

  return (
    <ItemsContainer
      alignItems="center"
      blockDesktopScrolling={!alwaysUseScrolling}
      mx={-itemMargin as Spacing}
      py={itemMargin as Spacing}
      my={-itemMargin as Spacing}
    >
      {showArrows && showLeftArrow && (
        <ArrowContainer left arrowOffset={arrowOffset}>
          <Button.Icon
            variant="primary"
            icon={<ArrowLeftIcon />}
            onClick={() => setCurrentIndex((currentIndex ?? 0) - 1)}
            aria-label="Scroll left"
          />
        </ArrowContainer>
      )}

      <CardsContainer
        data-testid="product-cards"
        flexBasis={flexBasis}
        dynamicWidth={dynamicWidth}
        ref={carousel}
        translateAmount={Math.min(
          (carousel.current?.offsetWidth || 0) * (currentIndex ?? 0),
          maxScrollWidth + cardGap // NOTE(elliot): Needs to scroll with margin at the end.
        )}
        itemWidth={itemWidth}
      >
        {carouselItems}
      </CardsContainer>
      {showArrows && showRightArrow && (
        <ArrowContainer arrowOffset={arrowOffset}>
          <Button.Icon
            variant="primary"
            icon={<ArrowRightIcon />}
            onClick={() => setCurrentIndex((currentIndex ?? 0) + 1)}
            aria-label="Scroll right"
          />
        </ArrowContainer>
      )}
    </ItemsContainer>
  );
};
